import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { emailTemplateAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "select2/dist/css/select2.min.css";
import "select2";
import $ from "jquery";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { sanitizeRichHtml } from "../utils/xss";
import "./RaiseComplaint.css";
import "./AddEditCountryMaster.css";

const AddEditEmailTemplate = () => {
  const navigate = useNavigate();
  const { templateCode } = useParams();
  const isEditMode = !!templateCode;

  const [template, setTemplate] = useState({
    templateCode: "",
    templateName: "",
    subject: "",
    bodyTemplate: "",
    eventType: "",
    isHTML: false,
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);

  const eventTypeSelectRef = useRef(null);
  const eventNameSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    loadEventTypes();
    if (isEditMode) {
      loadTemplate();
    } else {
      loadNextTemplateCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateCode]);

  const loadEventTypes = async () => {
    try {
      const response = await emailTemplateAPI.getEventTypes(true);
      if (response && response.success && response.data) {
        setEventTypes(response.data);
      }
    } catch (e) {
      console.error("Error loading event types:", e);
    }
  };


  const loadNextTemplateCode = async () => {
    try {
      const response = await emailTemplateAPI.getNextCode();
      if (response && response.success && response.data) {
        setTemplate((prev) => ({ ...prev, templateCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next template code:", e);
      // Set a default template code if API fails
      setTemplate((prev) => ({ ...prev, templateCode: `TEMPLATE-${Date.now()}` }));
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await emailTemplateAPI.getByCode(templateCode);
      if (response && response.success && response.data) {
        const data = response.data;
        setTemplate({
          templateCode: data.templateCode || "",
          templateName: data.templateName || "",
          subject: data.subject || "",
          bodyTemplate: sanitizeRichHtml(data.bodyTemplate || ""),
          eventType: data.eventType || "",
          isHTML: data.isHTML || false,
          isActive: data.isActive ?? true,
        });
      } else {
        navigate("/email-templates");
      }
    } catch (e) {
      console.error("Error loading template:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load template details", "error");
      }
      navigate("/email-templates");
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (field, value) => {
    setTemplate((prev) => ({
      ...prev,
      [field]: field === "bodyTemplate" ? sanitizeRichHtml(value) : value,
    }));
  };

  // Initialize Select2 for Notification Type dropdown
  useEffect(() => {
    if (eventTypeSelectRef.current) {
      const timer = setTimeout(() => {
        const $select = $(eventTypeSelectRef.current);
        if (!$select.length) return;
        
        const isSelect2Initialized = $select.data("select2");
        
        if (isSelect2Initialized) {
          // If already initialized, just update value if needed
          if (template.eventType && $select.val() !== template.eventType) {
            $select.val(template.eventType).trigger("change");
          }
          return;
        }
        
        // Initialize Select2 for the first time
        $select.select2({
          width: "100%",
          placeholder: "Select Notification Type",
          allowClear: false,
        });
        
        $select.on("change", function (e) {
          setTemplate((prev) => ({ ...prev, eventType: e.target.value || "" }));
        });
        
        // Set value after Select2 is initialized
        if (template.eventType) {
          setTimeout(() => {
            $select.val(template.eventType).trigger("change");
          }, 100);
        }
      }, 150);
      
      return () => {
        clearTimeout(timer);
        if (eventTypeSelectRef.current) {
          const $select = $(eventTypeSelectRef.current);
          if ($select.data("select2")) {
            try {
              $select.off("change");
              $select.select2("destroy");
            } catch (e) {
              // Ignore cleanup errors
            }
          }
        }
      };
    }
  }, [template.eventType]);

  // Initialize Select2 for Event Name dropdown
  useEffect(() => {
    if (eventNameSelectRef.current && eventTypes.length > 0) {
      const timer = setTimeout(() => {
        const $select = $(eventNameSelectRef.current);
        if (!$select.length) return;
        
        const isSelect2Initialized = $select.data("select2");
        
        if (isSelect2Initialized) {
          // If already initialized, just update value if needed
          if (template.templateName && $select.val() !== template.templateName) {
            $select.val(template.templateName).trigger("change");
          }
          return;
        }
        
        // Initialize Select2 for the first time
        $select.select2({
          width: "100%",
          placeholder: "Select Event Name",
          allowClear: false,
        });
        
        $select.on("change", function (e) {
          setTemplate((prev) => ({ ...prev, templateName: e.target.value || "" }));
        });
        
        // Set value after Select2 is initialized
        if (template.templateName) {
          setTimeout(() => {
            $select.val(template.templateName).trigger("change");
          }, 100);
        }
      }, 150);
      
      return () => {
        clearTimeout(timer);
        if (eventNameSelectRef.current) {
          const $select = $(eventNameSelectRef.current);
          if ($select.data("select2")) {
            try {
              $select.off("change");
              $select.select2("destroy");
            } catch (e) {
              // Ignore cleanup errors
            }
          }
        }
      };
    }
  }, [template.templateName, eventTypes]);

  const getTemplateTypeLabel = () => {
    return template.eventType === "Email" ? "Email template" : 
           template.eventType === "Notification" ? "Notification template" : 
           "Template";
  };

  const validateForm = async () => {
    if (!template.eventType?.trim()) {
      Swal.fire("Error", "Notification Type is required", "error");
      return false;
    }
    if (!template.templateName?.trim()) {
      Swal.fire("Error", "Event Name is required", "error");
      return false;
    }
    // Event code must exist in EVENTTYPEMASTER (same source as Event Name dropdown)
    const selectedEventCode = String(template.templateName).trim();
    const eventInMaster = eventTypes.some(
      (et) =>
        String(et.eventTypeCode ?? et.EventTypeCode ?? "").trim() === selectedEventCode &&
        (et.isActive === undefined ||
          et.IsActive === undefined ||
          et.isActive === true ||
          et.IsActive === true ||
          et.isActive === 1 ||
          et.IsActive === 1)
    );
    if (!eventInMaster) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: `Event "${selectedEventCode}" is not present (or not active) in Event Type Master. Add/activate it before saving this template.`,
      });
      return false;
    }
    if (template.eventType === "Email" && !template.subject?.trim()) {
      Swal.fire("Error", "Subject is required for Email templates", "error");
      return false;
    }
    
    // Check for duplicate Notification Type and Event Name combination
    try {
      const response = await emailTemplateAPI.getAll({
        eventType: template.eventType,
        pageNumber: 1,
        pageSize: 1000,
      });
      
      if (response?.success && response.data?.Templates) {
        const existingTemplates = response.data.Templates;
        const duplicate = existingTemplates.find(
          (t) => {
            if (isEditMode) {
              // For edit mode, exclude current template
              return t.templateCode !== template.templateCode && 
                     t.eventType === template.eventType && 
                     t.templateName === template.templateName;
            } else {
              // For new templates, check all
              return t.eventType === template.eventType && 
                     t.templateName === template.templateName;
            }
          }
        );
        
        if (duplicate) {
          const templateTypeLabel = getTemplateTypeLabel();
          Swal.fire({
            icon: "error",
            title: "Validation Error",
            text: `A ${templateTypeLabel.toLowerCase()} with the same Notification Type and Event Name already exists. Please select a different Event Name.`,
          });
          return false;
        }
      }
    } catch (error) {
      console.error("Error checking for duplicate templates:", error);
      // Continue with save if validation check fails (backend will also validate)
    }
    
    return true;
  };

  const handleSave = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    setSaving(true);
    try {
      const payload = {
        ...template,
        bodyTemplate: sanitizeRichHtml(template.bodyTemplate),
      };
      let response;

      if (isEditMode) {
        response = await emailTemplateAPI.update(payload.templateCode, payload);
      } else {
        response = await emailTemplateAPI.create(payload);
      }

      if (response && response.success) {
        const templateTypeLabel = getTemplateTypeLabel();
        Swal.fire({
          title: "Success",
          text: response.message || `${templateTypeLabel} saved successfully`,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/email-templates");
        });
      } else {
        const templateTypeLabel = getTemplateTypeLabel();
        Swal.fire(
          "Error",
          response.message || `Failed to save ${templateTypeLabel.toLowerCase()}`,
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving template:", e);
      const templateTypeLabel = getTemplateTypeLabel();
      const errorMessage = e.response?.data?.message || 
                          e.response?.data?.data?.message ||
                          `Failed to save ${templateTypeLabel.toLowerCase()}. Please try again.`;
      Swal.fire(
        "Error",
        errorMessage,
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/email-templates"
            onClick={(e) => {
              e.preventDefault();
              navigate("/email-templates");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Email Configuration
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Template" : "Add Template"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/email-templates")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className={`col-12 col-md-6 ${isEditMode ? "col-lg-3" : "col-lg-3"}`}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Notification Type <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={eventTypeSelectRef}
                  className="form-select form-control-light filter-select text-start"
                  style={{ height: "40px", textAlign: "left" }}
                >
                  <option value="">Select Notification Type</option>
                  <option value="Email">Email</option>
                  <option value="Notification">Notification</option>
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Event Name <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={eventNameSelectRef}
                  className="form-select form-control-light filter-select text-start"
                  style={{ height: "40px", textAlign: "left" }}
                >
                  <option value="">Select Event Name</option>
                  {eventTypes.map((eventType) => (
                    <option key={eventType.eventTypeCode} value={eventType.eventTypeCode}>
                      {eventType.eventTypeName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={`col-12 col-md-6 ${isEditMode ? "col-lg-3" : "col-lg-3"}`}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Subject {template.eventType === "Email" && <span style={{ color: "red" }}>*</span>}
                </label>
                <TextField
                  value={template.subject}
                  onChange={(e) => handleChange("subject", e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={template.eventType === "Email" ? "Enter email subject" : "Subject not required for notifications"}
                  className="add-edit-product-textfield"
                  required={template.eventType === "Email"}
                  disabled={template.eventType !== "Email"}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Body Template Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Body Template <span style={{ color: "red" }}>*</span>
                </label>
                {template.eventType === "Notification" ? (
                  <TextField
                    value={template.bodyTemplate || ""}
                    onChange={(e) => handleChange("bodyTemplate", e.target.value)}
                    fullWidth
                    multiline
                    placeholder="Notification body template. Use {variableName} for dynamic values."
                    className="add-edit-product-textfield"
                    required
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: "300px",
                        alignItems: "flex-start",
                        paddingTop: "8px",
                      },
                      "& .MuiInputBase-input": {
                        minHeight: "284px",
                        fontFamily: "inherit",
                        fontSize: "14px",
                        lineHeight: "1.5",
                        padding: "8px 12px",
                      },
                    }}
                  />
                ) : (
                  <div style={{ marginBottom: "8px" }}>
                    <ReactQuill
                      theme="snow"
                      value={template.bodyTemplate || ""}
                      onChange={(value) => handleChange("bodyTemplate", value)}
                      placeholder={`${template.eventType === "Email" ? "Email" : "Template"} body template. Use {variableName} for dynamic values.`}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ["bold", "italic", "underline", "strike"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [{ color: [] }, { background: [] }],
                          [{ align: [] }],
                          ["link", "image"],
                          ["clean"],
                        ],
                      }}
                      formats={[
                        "header",
                        "bold",
                        "italic",
                        "underline",
                        "strike",
                        "list",
                        "bullet",
                        "color",
                        "background",
                        "align",
                        "link",
                        "image",
                      ]}
                      style={{
                        backgroundColor: "#fff",
                        borderRadius: "4px",
                      }}
                    />
                    <style>{`
                      .ql-container {
                        min-height: 300px;
                        font-size: 14px;
                      }
                      .ql-editor {
                        min-height: 300px;
                      }
                      .ql-toolbar {
                        border-top-left-radius: 4px;
                        border-top-right-radius: 4px;
                        border: 1px solid #ced4da;
                        background-color: #f8f9fa;
                      }
                      .ql-container {
                        border-bottom-left-radius: 4px;
                        border-bottom-right-radius: 4px;
                        border: 1px solid #ced4da;
                        border-top: none;
                      }
                      .ql-editor.ql-blank::before {
                        color: #6c757d;
                        font-style: normal;
                      }
                    `}</style>
                  </div>
                )}
                <small className="form-text text-muted mt-1" style={{ display: "block" }}>
                  Use {"{variableName}"} syntax for dynamic values.
                  SR email header fields:{" "}
                  {"{ReceiverName}"}, {"{ChildSRCode}"}, {"{Status}"}, {"{CreatedBy}"}, {"{CreateDate}"},{" "}
                  {"{LastActionTaken}"}, {"{LastActionDate}"}, {"{BranchName}"}, {"{CustomerName}"},{" "}
                  {"{TypeOfSR}"}, {"{ProductName}"}, {"{InvoiceNumber}"}, {"{ApprovedValueTotal}"},{" "}
                  {"{ApproverName}"}, {"{PortalUrl}"}.
                  Product rows are injected automatically via {"{ProductDetailsHtml}"} (do not add product table rows in the template).
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/email-templates")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditEmailTemplate;
